/**
 * Firebase auth error helpers.
 *
 * Losing the network is not a bug — the auth screen already renders an offline
 * banner with a retry for it. This module is the single place that recognises
 * that failure, so the startup catch and the PostHog before_send filter agree
 * on what "offline" means.
 */

/** Firebase's code for "the auth request never reached the network". */
export const AUTH_NETWORK_ERROR_CODE = 'auth/network-request-failed';

/**
 * True for a Firebase offline auth failure.
 *
 * Accepts either an error object (`{ code }` / `Error`) or a bare string — the
 * PostHog before_send hook only ever sees the serialized `$exception_list`
 * values, never the original error.
 */
export function isNetworkAuthError(error: unknown): boolean {
  if (error === null || error === undefined) return false;

  const code = (error as { code?: unknown })?.code;
  if (code === AUTH_NETWORK_ERROR_CODE) return true;

  const message = (error as { message?: unknown })?.message;
  const text = typeof message === 'string' ? message : String(error);

  return text.includes(AUTH_NETWORK_ERROR_CODE);
}

/**
 * The messages a browser gives a fetch that never reached the server: Safari,
 * Chromium and Firefox, in that order. Matched as substrings because the text
 * is often wrapped by whoever logged it ("Translation failed: TypeError: Load
 * failed"); word-anchored so "Load failed" cannot match "upload failed".
 */
const OFFLINE_FETCH_RE =
  /\b(?:Load failed|Failed to fetch|NetworkError when attempting to fetch resource)\b/;

/**
 * True for a fetch that died before the server answered — the browser's own
 * wording, in any of the three engines. A server that answered 500 is not this.
 */
export function isOfflineFetchError(error: unknown): boolean {
  if (error === null || error === undefined) return false;
  const message = (error as { message?: unknown })?.message;
  const text = typeof message === 'string' ? message : String(error);
  return OFFLINE_FETCH_RE.test(text);
}
